<script setup lang="ts">
import { computed } from "vue";
import NavigationButtons from "@/entries/interact-form/components/NavigationButtons.vue";
import QuestionDisplay from "@/entries/interact-form/components/QuestionDisplay.vue";
import { useFormDataStore } from "@/entries/interact-form/stores/formData";

const props = defineProps<{
	hasAnsweredFirstQuestion: boolean;
	isModalOpen: boolean;
	thanksUrl?: string; // サンクスページURL（オプショナル）
	disableHeightLimit?: boolean; // 高さ制限解除フラグ（オプショナル）
	formTitle?: string; // ACFで設定されたフォームタイトル（オプショナル）
}>();

const emit = defineEmits<{
	(e: "openModal"): void;
	(e: "updateAnswer", id: number, answer: string | string[]): void;
	(e: "lastQuestionSubmit"): void;
}>();

const formDataStore = useFormDataStore();

/**
 * テキストをHTMLエスケープする
 * XSS対策用のヘルパー関数
 */
function escapeHtml(text: string): string {
	const div = document.createElement("div");
	div.textContent = text;
	return div.innerHTML;
}

/**
 * [br]デリミッターを<br>タグに変換する
 * H1TitleManager.phpと同様の処理をVue側で実装
 *
 * 処理フロー：
 * 1. [br]で文字列分割
 * 2. 各部分をトリム
 * 3. 空要素を除去
 * 4. escapeHtml()でXSS対策
 * 5. <br>で連結
 */
function convertLineBreaks(text: string): string {
	// [br]デリミッターで分割し、各部分をトリム
	const parts = text.split("[br]").map((part) => part.trim());
	// 空要素を除去
	const filteredParts = parts.filter((part) => part.length > 0);
	// XSS対策：各部分をエスケープ
	const escapedParts = filteredParts.map((part) => escapeHtml(part));
	// <br>で連結
	return escapedParts.join("<br>");
}

/**
 * H1タイトルテキストを動的に決定
 * ACFで設定されている場合はそれを優先、なければthanksUrlに基づいてフォールバック
 */
const displayTitle = computed(() => {
	// ACFで設定されている場合はそれを優先
	if (props.formTitle) {
		return props.formTitle;
	}
	// フォールバック: 従来のthanksUrl基準
	switch (props.thanksUrl) {
		case "/taiwa-thanks":
			return "カンタン回答で最適なサービスをご提案♪";
		case "/led-thanks":
			return "業界のプロが条件に合うLED業者を選定します";
		case "/kaitori-thanks":
			return "カンタンネット厨房機器査定！";
		default:
			// デフォルトは既存のテキスト
			return "カンタン回答で最適なサービスをご提案♪";
	}
});

/**
 * 表示用タイトル（[br]を<br>に変換済み）
 */
const formattedTitle = computed(() => {
	return convertLineBreaks(displayTitle.value);
});

/**
 * 回答更新のハンドラ
 *
 * 質問への回答が更新されたときに親コンポーネントに通知します
 *
 * @param {number} id - 質問のID
 * @param {string | string[]} answer - 更新する回答（文字列または文字列の配列）
 */
function handleAnswerUpdate(id: number, answer: string | string[]) {
	emit("updateAnswer", id, answer);
}
</script>

<template>
  <div class="interact-form initial-form">
    <h1 class="form-title" v-html="formattedTitle"></h1>

    <!-- クリックでモーダルを開くようにする -->
    <div class="question-wrapper" :class="{ 'no-height-limit': props.disableHeightLimit }" @click="hasAnsweredFirstQuestion && emit('openModal')">
      <!-- SP用コンシェルジュ画像とテキスト -->
      <div class="sp-concierge-container sp-only">
        <div class="concierge-image-wrapper">
          <img src="@/entries/interact-form/img/concierge.svg" alt="コンシェルジュ"
            class="sp-concierge-image" />
          <div class="concierge-text top-right">
            ご案内は全部無料
          </div>
        </div>
      </div>

      <QuestionDisplay :question="formDataStore.currentQuestion" :isModal="false" @update:answer="
        (answer) => handleAnswerUpdate(formDataStore.currentQuestion?.id || 0, answer)
      " @auto-next="formDataStore.nextQuestion" />

      <!-- PC用コンシェルジュ画像とテキスト -->
      <div class="top-concierge-container pc-only">
        <div class="concierge-image-wrapper">
          <img src="@/entries/interact-form/img/top_concierge.svg" alt="コンシェルジュ"
            class="top-concierge-image" />
          <div class="concierge-text top-left">
            ご案内は<br>全部無料
          </div>
        </div>
      </div>

      <!-- モーダルが閉じられた後で、かつ最初の質問に回答済みの場合にナビゲーションボタンを表示 -->
      <div v-if="hasAnsweredFirstQuestion && !isModalOpen" class="navigation-wrapper">
        <NavigationButtons :is-first-question="formDataStore.currentQuestionIndex === 0"
          :is-last-question="formDataStore.currentQuestionIndex === formDataStore.totalQuestions - 1
            " :is-next-disabled="formDataStore.isNextButtonDisabled"
          @prev="formDataStore.prevQuestion" @next="formDataStore.nextQuestion"
          @submit="emit('lastQuestionSubmit')" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.initial-form {
  padding: 0 20px;
  border-radius: 8px;
  margin: 20px 0;
  max-width: 900px;
  margin: 0 auto;
  background-color: transparent;
  position: relative;

  & > .question-wrapper > .question-area {
    border: 6px solid #a5dbdf;
    background-color: white;
    max-height: 400px;
    overflow-y: auto;
  }
  
  // 高さ制限解除時のスタイル
  & > .question-wrapper.no-height-limit > .question-area {
    max-height: none; // 高さ制限を解除
  }
}

.form-title {
  font-size: 28px;
  font-weight: bold;
  margin-top: 20px;
  margin-bottom: 10px;
  text-align: center;
  padding: 0 48px;
  position: relative;
}

.question-wrapper {
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  position: relative;

  &:hover {
    transform: translateY(-2px);
  }
}

.navigation-wrapper {
  margin-top: 15px;
}

// PC用コンシェルジュ画像コンテナ
.top-concierge-container {
  position: absolute;
  left: -160px;
  bottom: 40px;
  z-index: 1;
}

.concierge-image-wrapper {
  position: relative;
}

.top-concierge-image {
  width: 200px;
  height: auto;
}

.sp-concierge-container {
  position: absolute;
  top: -60px;
  left: 50%;
  transform: translateX(-50%);
  width: 250px;
  height: 66px;
  z-index: 2;
}

.sp-concierge-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

// コンシェルジュテキストスタイル
.concierge-text {
  position: absolute;
  font-weight: bold;
  font-size: 20px;
  color: #36B4A8;
  line-height: 1.2;
  background-color: transparent;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;

  &.top-left {
    top: 32px;
    left: 16px;
    white-space: pre-line; // 改行を有効にする
  }

  &.top-right {
    top: 8px;
    left:35%;

  }
}

// レスポンシブ表示切り替え
.pc-only {
  display: block;
}

.sp-only {
  display: none;
}

@media screen and (max-width: 1200px) {
.top-concierge-container {
  left: -40px;
  bottom: 40px;
  z-index: 1;
}
.top-concierge-image {
  width: 160px;
  height: auto;
}
.concierge-text {
  &.top-left {
  font-size: 16px;
  padding: 3px 6px;
  top:28px;
  left:14px;
  }
}
}
@media screen and (max-width: 767px) {
  .initial-form {
    padding: 0 10px;
    margin: 10px 0;
  }

  .form-title {
    font-size: 18px;
    margin-top: 15px;
    margin-bottom: 80px;
    padding: 0 15px;
  }

  .pc-only {
    display: none;
  }

  .sp-only {
    display: block;
  }

  .concierge-text {
    font-size: 16px;
    padding: 3px 6px;
  }
}
</style>
